// src/components/OrionVoiceWidget.tsx
// Widget de voz universal del dashboard — FAB abajo-derecha que abre
// VoiceChat (o MedicalVoiceChat para healthcare-clinic) en modal.
//
// ═══════════════════════════════════════════════════════════════════════
// 🔒 SINGLE-SOURCE · ai-agents:do-not-clone
// Toda sesión de voz del dashboard pasa por acá. Cambios en la lógica
// de detección healthcare van EN este archivo. Cambios de UI clínica
// → MedicalVoiceChat.tsx. Cambios de comportamiento WS/audio →
// useVoiceWebSocket.ts. NUNCA duplicar este componente.
// ═══════════════════════════════════════════════════════════════════════
//
// Detección healthcare = 2 fuentes, primer hit gana:
//   1. SYNC: `localStorage.org_id` matchea lista hardcoded de orgs
//      healthcare conocidas (`_HEALTHCARE_ORGS`). Disponible al render,
//      evita race con el fetch async de ModulesContext.
//   2. ASYNC: `useModules().archetype === 'healthcare-clinic'` cuando
//      el context termina de cargar. Confirma o adopta el shape médico.
//
// Cuando se onboardee una nueva clínica, agregar org_id a
// `_HEALTHCARE_ORGS` abajo (o reemplazar por endpoint GET /org/info
// que devuelva archetype y cachearlo en localStorage al login).

import { lazy, Suspense, useState } from 'react';
import { Loader2, Mic, Stethoscope, X } from 'lucide-react';

import { useBranding } from '@/context/BrandingContext';
import { useModules } from '@/context/ModulesContext';

const VoiceChat = lazy(() => import('@/components/VoiceChat'));
const MedicalVoiceChat = lazy(() => import('@/components/MedicalVoiceChat'));

// Lista de org_ids que sabemos son healthcare-clinic. Añadir aquí al
// onboardear nueva clínica para que el FAB monte UI médica SIN esperar
// el context async de /org/modules.
const _HEALTHCARE_ORGS = new Set([
  'clinic-demo',
  'medos-ai',
]);

function _isHealthcareOrg(): boolean {
  if (typeof window === 'undefined') return false;
  const stored = (localStorage.getItem('org_id') || '').toLowerCase();
  return _HEALTHCARE_ORGS.has(stored);
}

export function OrionVoiceWidget() {
  const { orgName } = useBranding();
  const { archetype } = useModules();
  const displayName = orgName || 'Asistente';
  // Sync detection primero (evita race); async lo confirma luego.
  const isHealthcare =
    _isHealthcareOrg() || archetype === 'healthcare-clinic';
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Hablar con ${displayName}`}
        title={
          isHealthcare
            ? 'Asistente clínico por voz'
            : `Hablar con ${displayName} (voz en tiempo real)`
        }
        className="fixed bottom-6 right-6 z-[60] flex items-center gap-2 rounded-full px-5 py-3 font-semibold text-white shadow-2xl transition-transform hover:scale-105"
        style={{
          background: isHealthcare
            ? 'linear-gradient(135deg, #2BB6A6 0%, #0F7B70 100%)'
            : 'linear-gradient(135deg, rgb(var(--acc1-rgb)) 0%, rgb(var(--acc2-rgb)) 100%)',
          boxShadow: isHealthcare
            ? '0 10px 30px rgba(43, 182, 166, 0.45)'
            : '0 10px 30px rgba(var(--acc1-rgb), 0.45)',
        }}
      >
        {isHealthcare ? <Stethoscope size={18} /> : <Mic size={18} />}
        <span className="hidden sm:inline">{isHealthcare ? 'Asistente' : 'Voz'}</span>
      </button>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
      style={{
        background: isHealthcare ? 'rgba(15, 42, 46, 0.55)' : 'rgba(2, 6, 23, 0.85)',
        backdropFilter: 'blur(8px)',
      }}
      role="dialog"
      aria-modal="true"
      aria-label={`Voz con ${displayName}`}
    >
      <div
        className="relative h-[min(90vh,800px)] w-full max-w-5xl overflow-hidden rounded-2xl"
        style={{
          background: isHealthcare ? '#FFFFFF' : 'rgba(15, 23, 42, 0.97)',
          border: isHealthcare
            ? '1px solid #CDE9E6'
            : '1px solid rgba(var(--acc1-rgb), 0.35)',
          boxShadow: isHealthcare
            ? '0 20px 60px rgba(15, 123, 112, 0.20)'
            : '0 20px 60px rgba(0,0,0,0.7)',
        }}
      >
        <div
          className="absolute inset-x-0 top-0 z-10 flex items-center justify-between px-4 py-2.5"
          style={{
            background: isHealthcare
              ? 'linear-gradient(135deg, #2BB6A6 0%, #0F7B70 100%)'
              : 'linear-gradient(135deg, rgba(var(--acc1-rgb), 0.85) 0%, rgba(var(--acc2-rgb), 0.85) 100%)',
          }}
        >
          <div className="flex items-center gap-2">
            {isHealthcare ? (
              <Stethoscope size={18} className="text-white" />
            ) : (
              <Mic size={18} className="text-white" />
            )}
            <div className="leading-tight">
              <div className="text-base font-semibold text-white">{displayName}</div>
              <div className="text-[10px] text-white/85">
                {isHealthcare
                  ? 'Asistente clínico · Pacientes · Agenda · Expediente'
                  : 'Voz en tiempo real · Conversación con tools'}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Cerrar voz"
            className="rounded-md p-1.5 text-white/95 hover:bg-white/15 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        <div className="absolute inset-0 pt-12">
          <Suspense
            fallback={
              <div
                className="flex h-full items-center justify-center"
                style={{ color: isHealthcare ? '#5A7A78' : 'rgba(255,255,255,0.6)' }}
              >
                <Loader2 size={24} className="mr-2 animate-spin" />
                {isHealthcare ? 'Preparando asistente clínico…' : 'Cargando voz…'}
              </div>
            }
          >
            {isHealthcare ? <MedicalVoiceChat /> : <VoiceChat />}
          </Suspense>
        </div>
      </div>
    </div>
  );
}
